<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, shallowRef, watch } from 'vue'
import type { UFPMMember, UFPMPlanEntry } from '../lib/types'
import type { PlanData } from '@/features/ai-plan/lib/types'
import AIPlanIsland from '@/features/ai-plan/AIPlanIsland.vue'
import { scoreEntryToResult } from '../lib/scoreEntryToResult'
import { exportPlanJson, exportPlanPdf, exportPlanXlsx, planDisplayName, sharePlanJson } from '../lib/ufpmPlanExport'

const props = defineProps<{
  sessionMemberIds?: readonly string[]
}>()

const members = shallowRef<readonly UFPMMember[]>([])
const search = ref('')
const showRosterSearch = ref(false)

function refreshMembers(): void {
  members.value = window.ufpmTools?.getMembers?.() ?? []
}

function onMembersChanged(event: Event): void {
  const next = (event as CustomEvent).detail?.members as UFPMMember[] | undefined
  if (next) members.value = next
}

onMounted(() => {
  refreshMembers()
  window.addEventListener('ptr:ufpm-members-changed', onMembersChanged)
})
onUnmounted(() => {
  window.removeEventListener('ptr:ufpm-members-changed', onMembersChanged)
})

const sessionMembers = computed(() => {
  const ids = props.sessionMemberIds ?? []
  return ids
    .map(id => members.value.find(m => m.id === id) ?? null)
    .filter((m): m is UFPMMember => m !== null)
})

const selectedMemberId = ref<string | null>(null)
const selectedMember = computed<UFPMMember | null>(
  () => members.value.find(m => m.id === selectedMemberId.value) ?? null
)

watch(sessionMembers, (list) => {
  if (!list.length) return
  if (!selectedMemberId.value || !list.some(m => m.id === selectedMemberId.value)) {
    selectedMemberId.value = list[0]!.id
  }
}, { immediate: true })

const rosterSearchResults = computed(() => {
  const q = search.value.trim().toLowerCase()
  if (!q) return []
  const sessionSet = new Set(props.sessionMemberIds ?? [])
  return members.value
    .filter(m => !sessionSet.has(m.id))
    .filter(m =>
      m.fullName?.toLowerCase().includes(q) ||
      m.rank?.toLowerCase().includes(q) ||
      m.organization?.toLowerCase().includes(q)
    )
    .slice(0, 8)
})

function selectMember(id: string): void {
  selectedMemberId.value = id
  selectedPlanIdx.value = 0
  window.ufpmTools?.selectMember?.(id)
}

const selectedPlanIdx = ref(0)
const shareBarRef = ref<HTMLElement | null>(null)
const selectedPlanEntry = computed<UFPMPlanEntry | null>(
  () => selectedMember.value?.plans?.[selectedPlanIdx.value] ?? null
)

const scoreOverride = computed(() => {
  const m = selectedMember.value
  if (!m) return null
  const entry = m.history?.[0]
  if (!entry) return null
  return scoreEntryToResult(entry, m)
})

const hasSavedScore = computed(() => !!selectedMember.value?.history?.[0])

async function handlePlanGenerated(plan: PlanData): Promise<void> {
  const m = selectedMember.value
  if (!m) return
  if (window.ufpmTools?.savePlanForMember?.(m.id, plan)) {
    selectedPlanIdx.value = 0
    refreshMembers()
    window.showToast?.('Plan saved for ' + m.fullName)
    await nextTick()
    shareBarRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

function memberLabel(m: UFPMMember): string {
  return [m.rank, m.fullName].filter(Boolean).join(' ') || 'Unknown'
}

function planLabel(entry: UFPMPlanEntry): string {
  return planDisplayName(entry.plan) + ' — ' +
    (entry.plan.total_weeks ? entry.plan.total_weeks + ' wks' : '') +
    (entry.createdAt ? ' · ' + new Date(entry.createdAt).toLocaleDateString() : '')
}

function doExportPdf(): void {
  const entry = selectedPlanEntry.value
  const name = selectedMember.value?.fullName ?? ''
  if (entry) exportPlanPdf(entry.plan, name)
}
function doExportXlsx(): void {
  const entry = selectedPlanEntry.value
  const name = selectedMember.value?.fullName ?? ''
  if (entry) exportPlanXlsx(entry.plan, name)
}
function doExportJson(): void {
  const entry = selectedPlanEntry.value
  const name = selectedMember.value?.fullName ?? ''
  if (entry) exportPlanJson(entry.plan, name)
}
function doShareJson(): void {
  const entry = selectedPlanEntry.value
  const name = selectedMember.value?.fullName ?? ''
  if (entry) void sharePlanJson(entry.plan, name)
}

function pickRosterMember(m: UFPMMember): void {
  selectMember(m.id)
  showRosterSearch.value = false
  search.value = ''
}
</script>

<template>
  <div class="ufpm-plans-panel">

    <!-- Session members (from active test session) -->
    <div v-if="sessionMembers.length" class="ufpm-plans-session">
      <div class="ufpm-plans-session-header">Test session</div>
      <div class="ufpm-plans-session-queue">
        <button
          v-for="m in sessionMembers"
          :key="m.id"
          class="ufpm-plans-session-chip"
          :class="{ 'ufpm-plans-session-chip--active': m.id === selectedMemberId }"
          type="button"
          @click="selectMember(m.id)"
        >
          <span class="ufpm-plans-session-chip__name">{{ memberLabel(m) }}</span>
          <span class="ufpm-plans-session-chip__meta">
            {{ m.plans?.length ?? 0 }} plan{{ m.plans?.length !== 1 ? 's' : '' }}
            <template v-if="m.history?.[0]"> · {{ Number(m.history[0].composite || 0).toFixed(1) }}</template>
          </span>
        </button>
      </div>
    </div>

    <!-- Collapsed roster search -->
    <div class="ufpm-plans-roster-search">
      <button
        class="ufpm-plans-roster-toggle"
        type="button"
        :aria-expanded="showRosterSearch"
        @click="showRosterSearch = !showRosterSearch"
      >
        {{ showRosterSearch ? '− Hide roster search' : '+ Search roster for other members' }}
      </button>
      <div v-if="showRosterSearch" class="ufpm-plans-roster-panel">
        <input
          v-model="search"
          type="search"
          class="ufpm-plans-search"
          placeholder="Type name or rank to search…"
          aria-label="Search roster members"
        />
        <div v-if="search.trim() && !rosterSearchResults.length" class="ufpm-empty">No matches outside the test session.</div>
        <button
          v-for="m in rosterSearchResults"
          :key="m.id"
          class="ufpm-plans-member-row"
          type="button"
          @click="pickRosterMember(m)"
        >
          <span class="ufpm-plans-member-name">{{ memberLabel(m) }}</span>
          <span class="ufpm-plans-member-meta">{{ m.organization || '' }}</span>
        </button>
      </div>
    </div>

    <!-- No selection -->
    <div v-if="!selectedMember" class="ufpm-empty" style="padding: 20px 0;">
      Add members to the test session or search the roster to draft a plan.
    </div>

    <!-- Selected member -->
    <div v-else class="ufpm-plans-detail">
      <div class="ufpm-plans-detail-header">
        <span class="ufpm-plans-detail-name">{{ memberLabel(selectedMember) }}</span>
      </div>

      <!-- Prominent share / export bar -->
      <div v-if="selectedPlanEntry" ref="shareBarRef" class="ufpm-plans-share-bar">
        <div class="ufpm-plans-share-bar__label">Share &amp; export plan</div>
        <div class="ufpm-plans-share-bar__actions">
          <button class="btn btn-primary ufpm-plans-share-btn" type="button" @click="doShareJson">Share Plan</button>
          <button class="btn btn-secondary ufpm-plans-share-btn" type="button" @click="doExportPdf">PDF</button>
          <button class="btn btn-secondary ufpm-plans-share-btn" type="button" @click="doExportXlsx">Excel</button>
          <button class="btn btn-secondary ufpm-plans-share-btn" type="button" @click="doExportJson">JSON</button>
        </div>
      </div>

      <div v-if="selectedMember.plans?.length" class="ufpm-plans-saved">
        <div class="ufpm-plans-saved-header">Saved Plans</div>
        <div
          v-for="(entry, idx) in selectedMember.plans"
          :key="entry.id"
          class="ufpm-plan-row"
          :class="{ 'ufpm-plan-row--active': idx === selectedPlanIdx }"
        >
          <button class="ufpm-plan-row-label" type="button" @click="selectedPlanIdx = idx">
            {{ planLabel(entry) }}
          </button>
        </div>
      </div>
      <div v-else class="ufpm-empty" style="margin-bottom: 12px;">
        No saved plans for {{ selectedMember.fullName }} yet.
      </div>

      <div class="ufpm-plans-generate">
        <div class="ufpm-plans-generate-header">Generate New Plan</div>
        <div v-if="!hasSavedScore" class="ufpm-calc-notice">
          Save a test score for this member first (Test → Calc) so the plan can use their latest PFRA result.
        </div>
        <AIPlanIsland
          :key="'plans-' + selectedMember.id"
          :score-override="scoreOverride"
          :persist-active-plan="false"
          :show-load-action="false"
          :show-import-shortcut="false"
          :show-export-actions="false"
          :context-label="selectedMember.fullName || 'selected member'"
          @plan-generated="handlePlanGenerated"
        />
      </div>
    </div>

  </div>
</template>